/**
 * Container Size Selector Component
 *
 * Visual, mobile-friendly container size selection with real-world comparisons
 * to help customers choose the right size for their needs.
 */

import { cn } from '../../lib/utils';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { InfoIcon, CheckCircleIcon, ArrowLeftRight } from 'lucide-react';
import { useState } from 'react';
import { SocialProofBadge, type ProofType } from '../social/SocialProofBadge';

export interface ContainerSize {
  id: string;
  size: string;
  description: string;
  dimensions: {
    length: number;
    width: number;
    height: number;
  };
  capacity: {
    tons: string;
    truckLoads: string;
    cubicYards: number;
  };
  commonUses: string[];
  price?: string;
  available: boolean;
  recommended?: boolean;
  socialProof?: {
    type: ProofType;
    value?: string | number;
  };
}

interface ContainerSizeSelectorProps {
  sizes: ContainerSize[];
  selectedSize: string | null;
  onSelect: (sizeId: string) => void;
  showPricing?: boolean;
  showComparison?: boolean;
  onCompare?: () => void;
}

const ContainerIcon = ({ size }: { size: string }) => {
  // Visual representation scales with container size
  const scale = size === '10Y' ? 0.6 : size === '20Y' ? 0.8 : size === '30Y' ? 0.9 : 1;

  return (
    <svg
      viewBox="0 0 100 60"
      className="w-full h-full"
      style={{ transform: `scaleX(${scale})` }}
    >
      <rect
        x="10"
        y="20"
        width="80"
        height="30"
        fill="currentColor"
        className="text-primary"
        rx="2"
      />
      <rect
        x="10"
        y="20"
        width="80"
        height="5"
        fill="currentColor"
        className="text-primary-700"
      />
      <circle cx="25" cy="50" r="4" fill="currentColor" className="text-gray-700" />
      <circle cx="75" cy="50" r="4" fill="currentColor" className="text-gray-700" />
    </svg>
  );
};

export function ContainerSizeSelector({
  sizes,
  selectedSize,
  onSelect,
  showPricing = false,
  showComparison = false,
  onCompare,
}: ContainerSizeSelectorProps) {
  const [showDetails, setShowDetails] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            What size container do you need?
          </h2>
          <p className="text-sm text-gray-600">
            Choose based on your project size. Not sure? Our team can help when they call to confirm.
          </p>
        </div>
        {showComparison && onCompare && sizes.length > 1 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCompare}
            className="flex items-center gap-2 whitespace-nowrap"
          >
            <ArrowLeftRight className="w-4 h-4" />
            Compare Sizes
          </Button>
        )}
      </div>

      {/* Size Cards Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {sizes.map((size) => (
          <Card
            key={size.id}
            className={cn(
              'relative cursor-pointer transition-all duration-200',
              'hover:shadow-lg hover:scale-[1.02]',
              'focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2',
              selectedSize === size.id && 'ring-2 ring-primary shadow-lg',
              !size.available && 'opacity-60 cursor-not-allowed'
            )}
            onClick={() => size.available && onSelect(size.id)}
          >
            {/* Social Proof Badge */}
            {size.socialProof && (
              <div className="absolute -top-2 -right-2 z-10">
                <SocialProofBadge
                  type={size.socialProof.type}
                  value={size.socialProof.value}
                  size="sm"
                  variant="prominent"
                />
              </div>
            )}

            {/* Fallback to recommended badge if no social proof */}
            {!size.socialProof && size.recommended && (
              <Badge
                className="absolute -top-2 -right-2 z-10"
                variant="default"
              >
                Most Popular
              </Badge>
            )}

            {!size.available && (
              <Badge
                className="absolute -top-2 -left-2 z-10"
                variant="secondary"
              >
                Unavailable
              </Badge>
            )}

            <CardContent className="p-6">
              <button
                type="button"
                className="w-full text-left focus:outline-none"
                disabled={!size.available}
                aria-pressed={selectedSize === size.id}
                aria-label={`${size.size} container, ${size.capacity.truckLoads} pickup truck loads`}
              >
                {/* Size Header */}
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{size.size}</h3>
                    <p className="text-sm text-gray-600">{size.description}</p>
                  </div>
                  {selectedSize === size.id && (
                    <CheckCircleIcon className="w-6 h-6 text-primary" />
                  )}
                </div>

                {/* Visual Container Icon */}
                <div className="h-16 w-full mb-4">
                  <ContainerIcon size={size.size} />
                </div>

                {/* Capacity Info */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Capacity:</span>
                    <span className="font-medium text-gray-900">
                      {size.capacity.truckLoads} pickup trucks
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Holds:</span>
                    <span className="font-medium text-gray-900">
                      {size.capacity.tons} tons
                    </span>
                  </div>
                  {showPricing && size.price && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Starting at:</span>
                      <span className="font-bold text-gray-900">{size.price}</span>
                    </div>
                  )}
                </div>

                {/* Common Uses */}
                <div className="border-t pt-3">
                  <p className="text-xs text-gray-600 mb-2">Perfect for:</p>
                  <div className="flex flex-wrap gap-1">
                    {size.commonUses.slice(0, 2).map((use, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        {use}
                      </Badge>
                    ))}
                    {size.commonUses.length > 2 && (
                      <Badge variant="outline" className="text-xs">
                        +{size.commonUses.length - 2}
                      </Badge>
                    )}
                  </div>
                </div>
              </button>

              {/* More Details Button */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDetails(showDetails === size.id ? null : size.id);
                }}
                className="mt-3 text-xs text-primary hover:underline focus:outline-none focus:underline flex items-center gap-1"
              >
                <InfoIcon className="w-3 h-3" />
                {showDetails === size.id ? 'Hide' : 'Show'} details
              </button>

              {/* Expandable Details */}
              {showDetails === size.id && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm space-y-2">
                  <div>
                    <p className="font-medium text-gray-700">Dimensions:</p>
                    <p className="text-gray-600">
                      {size.dimensions.length}&apos;L × {size.dimensions.width}&apos;W × {size.dimensions.height}&apos;H
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-700">Best for:</p>
                    <ul className="text-gray-600 list-disc list-inside">
                      {size.commonUses.map((use, index) => (
                        <li key={index}>{use}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Size Comparison Helper */}
      {showComparison && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <InfoIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900 mb-1">
                  Not sure which size you need?
                </p>
                <p className="text-sm text-blue-800">
                  A good rule of thumb: 1 room renovation = 10 yard, small home project = 20 yard,
                  whole house cleanout = 30 yard or larger.
                </p>
                <Button
                  type="button"
                  variant="link"
                  className="p-0 h-auto text-blue-700 hover:text-blue-800"
                  onClick={() => {
                    // This would open a size calculator modal
                    console.log('Open size calculator');
                  }}
                >
                  Use our size calculator →
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Selected Size Summary */}
      {selectedSize && (
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="w-5 h-5 text-green-600" />
              <p className="text-sm font-medium text-green-900">
                Selected: {sizes.find(s => s.id === selectedSize)?.size} Container
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}